import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Calendar03Icon,
  Copy01Icon,
  Loading01Icon,
  Tick01Icon,
} from "@hugeicons/core-free-icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import {
  getPortalLink,
  postCancelIntent,
  type CancelIntentResult,
} from "@/lib/api/billing";
import { sendDeleteAccountRequest, sendFeedback } from "@/lib/api/api";
import { track } from "@/lib/utils/track";
import { AMIR_CALL_URL } from "@/lib/constants/support";
import { useCancelUsageLoss } from "@/hooks/useCancelUsageLoss";
import {
  CANCELLATION_REASONS,
  RETENTION_COUPON,
  callOfferCopy,
  formatCancellationReason,
  recoveryOfferFor,
  type CancellationReason,
  type CancelFlowMode,
  type CancelOutcome,
  type CancelSurface,
  type RecoveryOffer,
} from "@/lib/constants/cancellationReasons";
import {
  logBillingEvent,
  reportBillingFailure,
  useBillingScope,
} from "@/lib/observability/billing";

type Step = "reason" | "offer" | "last-try" | "coupon-copied";

type CancelFlowDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: CancelFlowMode;
  surface: CancelSurface;
};

export default function CancelFlowDialog({
  open,
  onOpenChange,
  mode,
  surface,
}: CancelFlowDialogProps) {
  const { token } = useAuth();
  const { stripeSubscriptionId } = useSubscription();
  const scope = useBillingScope();
  const { copy: lossCopy } = useCancelUsageLoss(open);
  const prevOpen = useRef(false);
  // Bumped on every reset so a request that resolves after the dialog closed
  // can't write its step into the next session.
  const sessionRef = useRef(0);

  const [step, setStep] = useState<Step>("reason");
  const [reason, setReason] = useState<CancellationReason | "">("");
  const [reasonDetail, setReasonDetail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const offer: RecoveryOffer | null = reason ? recoveryOfferFor(reason) : null;
  const canContinueFromReason =
    !!reason && (reason !== "other" || reasonDetail.trim().length > 0);
  const totalSteps = 3;
  const stepNumber = step === "reason" ? 1 : step === "offer" ? 2 : 3;
  const callCopy = callOfferCopy(reason);

  const reset = useCallback(() => {
    sessionRef.current += 1;
    setStep("reason");
    setReason("");
    setReasonDetail("");
    setIsSubmitting(false);
  }, []);

  useEffect(() => {
    if (open && !prevOpen.current) {
      track("cancel_flow_opened", token, { mode, surface });
      logBillingEvent("cancel_flow_opened", scope, { mode, surface });
    }
    if (!open && prevOpen.current) {
      reset();
    }
    prevOpen.current = open;
  }, [open, mode, reset, scope, surface, token]);

  const recordIntent = useCallback(
    async (
      outcome: CancelOutcome,
      offerAccepted: boolean,
    ): Promise<CancelIntentResult | null> => {
      if (!token || !reason || !offer) return null;
      try {
        return await postCancelIntent(token, {
          reason,
          // The backend rejects a detail on any reason but "other", and the
          // textarea keeps its text when the user switches reasons.
          reason_detail: reason === "other" ? reasonDetail.trim() || null : null,
          offer_shown: offer,
          offer_accepted: offerAccepted,
          surface,
          mode,
          outcome,
        });
      } catch (error) {
        reportBillingFailure("record_cancel_intent", error, scope, {
          mode,
          surface,
          reason,
          offer,
          outcome,
        });
        return null;
      }
    },
    [mode, offer, reason, reasonDetail, scope, surface, token],
  );

  const logOfferAccepted = useCallback(
    (outcome: CancelOutcome, discountApplied?: boolean) => {
      track("cancel_offer_accepted", token, {
        mode,
        surface,
        reason,
        offer,
        outcome,
        ...(discountApplied !== undefined && { discount_applied: discountApplied }),
      });
      logBillingEvent("cancel_offer_accepted", scope, {
        mode,
        surface,
        reason: reason || null,
        offer,
        outcome,
        discountApplied: discountApplied ?? null,
      });
    },
    [mode, offer, reason, scope, surface, token],
  );

  const bookCall = useCallback(() => {
    window.open(AMIR_CALL_URL, "_blank", "noopener,noreferrer");
    // A call booked from the discount path is the last-try call after the
    // discount was declined, which the backend expects as offer_accepted: false.
    void recordIntent("retained_call", offer === "call");
    logOfferAccepted("retained_call");
    toast.success("We'll see you on the call.");
    onOpenChange(false);
  }, [logOfferAccepted, offer, onOpenChange, recordIntent]);

  const trackHelpCall = useCallback(() => {
    track("cancel_help_call_clicked", token, { mode, surface, reason, offer, step });
  }, [mode, offer, reason, step, surface, token]);

  // Static coupon: the customer pastes it in the Stripe portal themselves. No
  // cancel-intent call here, because a `retained_discount` accept makes the
  // backend apply the coupon programmatically.
  const copyCoupon = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(RETENTION_COUPON.code);
      return true;
    } catch {
      toast.error(`Couldn't copy. The code is ${RETENTION_COUPON.code}.`);
      return false;
    }
  }, []);

  const claimCoupon = useCallback(async () => {
    if (!token) return;
    setIsSubmitting(true);
    const copied = await copyCoupon();
    track("cancel_coupon_copied", token, { mode, surface, reason, offer, code: RETENTION_COUPON.code });
    logBillingEvent("cancel_coupon_copied", scope, { mode, surface, reason: reason || null, offer });
    let newWindow: Window | null = null;
    try {
      newWindow = window.open("", "_blank");
      const { url } = await getPortalLink(token, { scope });
      if (!url) throw new Error("Portal link response missing url");
      if (newWindow) newWindow.location.href = url;
      else window.location.href = url;
    } catch (error) {
      newWindow?.close();
      toast.error("Couldn't open billing. Your code is still copied.");
      reportBillingFailure("open_portal", error, scope, { mode, surface, reason: reason || null, offer });
    } finally {
      setIsSubmitting(false);
    }
    if (copied) setStep("coupon-copied");
  }, [copyCoupon, mode, offer, reason, scope, surface, token]);

  const openPortal = useCallback(async () => {
    if (!token) return;
    setIsSubmitting(true);
    let newWindow: Window | null = null;
    try {
      newWindow = window.open("", "_blank");
      const { url, flow } = await getPortalLink(token, {
        flow: "cancel",
        subscriptionId: stripeSubscriptionId,
        scope,
      });
      if (url) {
        if (newWindow) {
          newWindow.location.href = url;
        } else {
          window.location.href = url;
        }
        void recordIntent("handed_off", false);
        track("cancel_portal_handoff", token, { mode, surface, reason, offer });
        logBillingEvent("cancel_portal_handoff", scope, {
          mode,
          surface,
          reason: reason || null,
          offer,
          outcome: "handed_off",
          portalFlow: flow,
        });
        onOpenChange(false);
      } else {
        newWindow?.close();
        toast.error("Couldn't open billing. Please try again.");
        reportBillingFailure(
          "open_portal",
          new Error("Portal link response missing url"),
          scope,
          { mode, surface, reason: reason || null, offer },
        );
      }
    } catch (error) {
      newWindow?.close();
      toast.error("Couldn't open billing. Please try again.");
      reportBillingFailure("open_portal", error, scope, {
        mode,
        surface,
        reason: reason || null,
        offer,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [mode, offer, onOpenChange, reason, recordIntent, scope, stripeSubscriptionId, surface, token]);

  const submitDelete = useCallback(async () => {
    if (!token || !reason) return;
    setIsSubmitting(true);
    try {
      await sendFeedback("Account Delete Request", token);
      await sendDeleteAccountRequest(
        token,
        formatCancellationReason(reason, reasonDetail),
      );
      void recordIntent("handed_off", false);
      track("delete_request_submitted", token, { mode, surface, reason, offer });
      logBillingEvent("delete_request_submitted", scope, {
        mode,
        surface,
        reason,
        offer,
        outcome: "handed_off",
      });
      toast.success("Delete request sent.");
      onOpenChange(false);
    } catch (error) {
      toast.error(
        "There was an error sending your delete request. Please try again later.",
      );
      reportBillingFailure("submit_delete_request", error, scope, {
        mode,
        surface,
        reason,
        offer,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [mode, offer, onOpenChange, reason, reasonDetail, recordIntent, scope, surface, token]);

  const handoff = useCallback(async () => {
    if (mode === "delete") {
      await submitDelete();
      return;
    }
    await openPortal();
  }, [mode, openPortal, submitDelete]);

  const continueFromReason = () => {
    if (!reason || !offer) return;
    track("cancel_reason_selected", token, { mode, surface, reason, offer });
    logBillingEvent("cancel_reason_selected", scope, {
      mode,
      surface,
      reason,
      offer,
    });
    track("cancel_offer_shown", token, { mode, surface, reason, offer });
    logBillingEvent("cancel_offer_shown", scope, {
      mode,
      surface,
      reason,
      offer,
    });
    setStep("offer");
  };

  const isDelete = mode === "delete";
  const title =
    step === "coupon-copied"
      ? "Code copied"
      : step === "reason"
      ? isDelete
        ? "Before you delete your account"
        : "Before you cancel"
      : step === "offer" && lossCopy
        ? lossCopy
        : step === "offer"
          ? offer === "discount"
            ? "Stay for less"
            : callCopy.heading
          : "One last try";

  const description =
    step === "coupon-copied"
      ? "Billing opened in a new tab. Paste the code there to apply the discount to your plan."
      : step === "reason"
      ? isDelete
        ? "Tell us why you're leaving. We'll process deletion within 48 hours if you still want to go."
        : "Tell us why you're leaving. It takes a few seconds and helps us keep the product useful."
      : step === "offer" && offer === "discount"
          ? `Keep your plan for ${RETENTION_COUPON.offer}. Copy the code and add it in billing.`
          : step === "offer"
            ? callCopy.caption
            : "Teams who book a setup call usually stay. One more chance before you go.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          {step !== "coupon-copied" && (
            <p className="text-xs font-medium leading-4 text-muted-foreground">
              Step {stepNumber} of {totalSteps}
            </p>
          )}
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {step === "reason" && (
          <div className="space-y-3">
            <div className="flex flex-col gap-1.5">
              {CANCELLATION_REASONS.map((item) => {
                const selected = reason === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setReason(item.value)}
                    className={cn(
                      "flex h-10 w-full items-center rounded-lg px-3 text-left text-sm text-foreground transition-[box-shadow,background-color]",
                      selected
                        ? "bg-card shadow-[0_0_0_1.5px_var(--foreground)]"
                        : "bg-card shadow-[0_0_0_1px_var(--input)] hover:bg-muted/50",
                    )}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
            {reason === "other" && (
              <div className="space-y-1.5">
                <Label htmlFor="cancel-reason-detail" className="text-xs">
                  Please specify
                </Label>
                <Textarea
                  id="cancel-reason-detail"
                  value={reasonDetail}
                  onChange={(e) => setReasonDetail(e.target.value)}
                  placeholder="What's going on?"
                  className="min-h-16"
                />
              </div>
            )}
            <Button
              className="w-full h-9"
              disabled={!canContinueFromReason}
              onClick={continueFromReason}
            >
              Continue
              <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
            </Button>
          </div>
        )}

        {step === "offer" && offer === "discount" && (
          <div className="flex flex-col gap-3">
            <CouponCode onCopy={copyCoupon} />
            <Button className="h-9 w-full gap-2" onClick={() => void claimCoupon()} disabled={isSubmitting}>
              {isSubmitting ? (
                <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />
              ) : null}
              Copy code and open billing
              <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
            </Button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => setStep("last-try")}
              className="block w-full text-center text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              Continue cancelling
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => setStep("reason")}
              className="flex w-full items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} size={12} />
              Back
            </button>
            <CallHelp onClick={trackHelpCall} />
          </div>
        )}

        {step === "offer" && offer === "call" && (
          <OfferBody
            icon={Calendar03Icon}
            heading={callCopy.heading}
            caption={callCopy.caption}
            primaryLabel="Pick a time"
            secondaryLabel="Continue cancelling"
            onPrimary={bookCall}
            onSecondary={() => setStep("last-try")}
            onBack={() => setStep("reason")}
          />
        )}

        {step === "last-try" && (
          <OfferBody
            icon={Calendar03Icon}
            heading="Book a 30-minute call before you go"
            caption="This is the last step. If it still isn't the right fit, you can finish cancelling after."
            primaryLabel="Pick a time"
            secondaryLabel={
              isDelete ? "Submit deletion request" : "Continue to cancel"
            }
            secondaryDestructive
            loading={isSubmitting}
            onPrimary={bookCall}
            onSecondary={() => void handoff()}
            onBack={() => setStep("offer")}
          />
        )}

        {step === "coupon-copied" && (
          <div className="flex flex-col gap-3">
            <ol className="flex flex-col gap-2 rounded-xl bg-muted/60 px-4 py-3.5 text-[13px] leading-5 text-foreground">
              {[
                "In billing, open your current plan.",
                <>Choose <span className="font-medium">Add promotion code</span>.</>,
                <>Paste <span className="font-mono font-medium">{RETENTION_COUPON.code}</span> and confirm.</>,
              ].map((line, i) => (
                <li key={i} className="flex gap-2.5">
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold shadow-[inset_0_0_0_1.5px_currentColor]">
                    {i + 1}
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ol>
            <CouponCode onCopy={copyCoupon} />
            <Button className="h-9 w-full" onClick={() => onOpenChange(false)}>
              Done
            </Button>
            <button
              type="button"
              onClick={() => setStep("last-try")}
              className="block w-full text-center text-xs text-muted-foreground hover:text-foreground"
            >
              Continue cancelling
            </button>
            <CallHelp onClick={trackHelpCall} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Quiet "talk to a person" line under billing steps. */
function CallHelp({ onClick }: { onClick: () => void }) {
  return (
    <p className="border-t border-border pt-3 text-center text-xs leading-4 text-foreground/60">
      Need help with billing?{" "}
      <a
        href={AMIR_CALL_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className="font-medium text-foreground underline underline-offset-2 hover:text-foreground/70"
      >
        Book a call with Amir
      </a>
    </p>
  );
}

/** The retention code in a raised box with its own copy button. */
function CouponCode({ onCopy }: { onCopy: () => Promise<boolean> }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(id);
  }, [copied]);
  return (
    <div className="flex items-center gap-3 rounded-xl bg-card px-4 py-3 shadow-[0_0_0_1px_var(--input)]">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-xs leading-4 text-foreground/60">{RETENTION_COUPON.offer}</span>
        <span className="font-mono text-lg font-semibold leading-6 tracking-[0.06em]">{RETENTION_COUPON.code}</span>
      </div>
      <Button
        variant="outline"
        className="h-[34px] shrink-0 gap-1.5 rounded-lg border-input bg-background px-3 text-xs font-medium shadow-none"
        onClick={() => void onCopy().then((ok) => ok && setCopied(true))}
      >
        <HugeiconsIcon icon={copied ? Tick01Icon : Copy01Icon} size={13} />
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

type OfferBodyProps = {
  icon: IconSvgElement;
  heading: string;
  caption: string;
  primaryLabel: string;
  secondaryLabel: string;
  secondaryDestructive?: boolean;
  loading?: boolean;
  onPrimary: () => void;
  onSecondary: () => void;
  onBack: () => void;
};

function OfferBody({
  icon,
  heading,
  caption,
  primaryLabel,
  secondaryLabel,
  secondaryDestructive = false,
  loading = false,
  onPrimary,
  onSecondary,
  onBack,
}: OfferBodyProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3 rounded-md border border-border/40 bg-muted/40 px-3 py-2.5">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border border-border/40 bg-muted/40">
          <HugeiconsIcon icon={icon} size={14} className="text-muted-foreground" />
        </span>
        <div>
          <p className="text-sm font-medium text-foreground">{heading}</p>
          <p className="text-xs text-muted-foreground leading-relaxed">{caption}</p>
        </div>
      </div>
      <Button className="w-full h-9 gap-2" onClick={onPrimary} disabled={loading}>
        {primaryLabel}
        <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
      </Button>
      <button
        type="button"
        disabled={loading}
        onClick={onSecondary}
        className={cn(
          "block w-full text-center text-xs hover:text-foreground disabled:opacity-50",
          secondaryDestructive
            ? "text-destructive"
            : "text-muted-foreground",
        )}
      >
        {loading ? (
          <span className="inline-flex items-center justify-center gap-1.5">
            <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin" />
            Working…
          </span>
        ) : (
          secondaryLabel
        )}
      </button>
      <button
        type="button"
        disabled={loading}
        onClick={onBack}
        className="flex w-full items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
      >
        <HugeiconsIcon icon={ArrowLeft01Icon} size={12} />
        Back
      </button>
    </div>
  );
}
